/**
 * Invoicing, payments, financial adjustments and client statements — Phase 11
 * (decisions D143–D155, D159, D160).
 *
 * ===========================================================================
 * MONEY IS AN INTEGER NUMBER OF MINOR UNITS. ALWAYS.
 * ===========================================================================
 *
 * Nothing in this file adds, multiplies or rounds a floating-point amount of
 * money. Quantities (`numeric(12,3)` in the database) and percentages
 * (`numeric(5,2)`) travel as DECIMAL TEXT and are turned into exact scaled
 * integers (`bigint`) before any arithmetic; the only rounding is HALF UP to a
 * whole minor unit — the same rule as `roundMinor` in `pricing.ts`, applied to
 * the EXACT product rather than to a floating-point approximation of it. So
 * `1.005 × 100` is 100.5 and rounds to 101, where `Math.round(1.005 * 100)`
 * would say 100.
 *
 * Six ideas govern this file, each an acceptance criterion or a recorded
 * decision rather than a preference:
 *
 *  1. ONE ARITHMETIC (D151). Line subtotal = quantity × unit price, rounded
 *     half up; then the line discount; then the document discount on what
 *     remains; then tax on what remains after that — the quotation's order
 *     (D43). The invoice, the statement and the portal all read figures this
 *     arithmetic produced, so they cannot disagree.
 *
 *  2. A DISCOUNT LARGER THAN ITS AMOUNT IS REFUSED, NEVER CLAMPED (D151).
 *     `computeInvoiceTotals` throws `DiscountExceedsAmountError`, naming the
 *     line or the document. Reducing what a client owes beyond that is a
 *     recorded adjustment (D154), with its own permission and reason.
 *
 *  3. PAYMENT STATUS AND OVERDUE ARE DERIVED, NEVER STORED (D152). A stored
 *     status needs a job to keep it true, and the day the job fails it lies.
 *
 *  4. THE STATEMENT IS ONE COMPUTATION over one list of entries (D155), for the
 *     staff screen and the portal alike: `buildStatement`.
 *
 *  5. PAYMENT STATUS IS INDEPENDENT OF TEST AND REPORT STATUS. Nothing here
 *     reads a result, a report or a test, and nothing here is test science.
 *
 *  6. AN INVOICE HERE IS A FINANCIAL RECORD, NOT A TAX INVOICE (D160). No PDF,
 *     no e-invoicing QR, nothing sent to a tax authority.
 *
 * The BACKEND is the authority. These functions are shared so the interface
 * can show running figures, but every stored figure is recomputed on the
 * server from the stored inputs. The client never sends a total.
 */
import { INVOICE_STATUS, PAYMENT_STATUS, } from './status.js';
// ---------------------------------------------------------------------------
// Enumerations
// ---------------------------------------------------------------------------
/** How money was received (D153). The laboratory's own receipt number goes in
 *  the payment's `reference`; payments take no generated number. */
export const PAYMENT_METHOD = {
    CASH: 'cash',
    BANK_TRANSFER: 'bank_transfer',
    CHEQUE: 'cheque',
    CARD: 'card',
    OTHER: 'other',
};
export const ALL_PAYMENT_METHODS = Object.freeze(Object.values(PAYMENT_METHOD));
/**
 * Where the unit price on an invoice line came from (D151).
 *
 * Recorded per line, because "the price the work order froze" and "somebody
 * with `pricing.override` changed it, and here is why" are different facts.
 */
export const INVOICE_PRICE_SOURCE = {
    /** The unit price the work order froze for the service. */
    SERVICE: 'service',
    /** Different from the service's price: needs `pricing.override` AND a reason. */
    OVERRIDE: 'override',
    /** A line with no service behind it (a site visit, a courier charge). It
     *  cannot be duplicate-checked, because there is nothing to check it against. */
    MANUAL: 'manual',
};
export const ALL_INVOICE_PRICE_SOURCES = Object.freeze(Object.values(INVOICE_PRICE_SOURCE));
/**
 * A financial adjustment against an issued invoice (D154). A CREDIT lowers
 * what the client owes and may not exceed the invoice's balance; a DEBIT
 * raises it. Append-only: a mistaken one is corrected by an opposite one. An
 * adjustment changes what is owed, NOT the tax shown on the invoice.
 */
export const ADJUSTMENT_KIND = {
    CREDIT: 'credit',
    DEBIT: 'debit',
};
export const ALL_ADJUSTMENT_KINDS = Object.freeze(Object.values(ADJUSTMENT_KIND));
/**
 * The kinds of entry on a client's account — the rows of the database view
 * `client_account_entries` (D155). Allocations are deliberately NOT entries:
 * they move money between the account and its invoices, not in or out of it.
 */
export const ACCOUNT_ENTRY_KIND = {
    /** Debit of the invoice's total, dated its issue date. */
    INVOICE_ISSUED: 'invoice_issued',
    /** Credit of what the invoice still carried, dated the day it was cancelled. */
    INVOICE_CANCELLED: 'invoice_cancelled',
    /** Credit of the amount received, dated the day it was received. */
    PAYMENT_RECEIVED: 'payment_received',
    /** Debit of a payment recorded in error, dated the day it was reversed. */
    PAYMENT_REVERSED: 'payment_reversed',
    ADJUSTMENT_CREDIT: 'adjustment_credit',
    ADJUSTMENT_DEBIT: 'adjustment_debit',
};
export const ALL_ACCOUNT_ENTRY_KINDS = Object.freeze(Object.values(ACCOUNT_ENTRY_KIND));
/** The side of the account each kind of entry falls on. */
export const ACCOUNT_ENTRY_SIDE = Object.freeze({
    [ACCOUNT_ENTRY_KIND.INVOICE_ISSUED]: 'debit',
    [ACCOUNT_ENTRY_KIND.INVOICE_CANCELLED]: 'credit',
    [ACCOUNT_ENTRY_KIND.PAYMENT_RECEIVED]: 'credit',
    [ACCOUNT_ENTRY_KIND.PAYMENT_REVERSED]: 'debit',
    [ACCOUNT_ENTRY_KIND.ADJUSTMENT_CREDIT]: 'credit',
    [ACCOUNT_ENTRY_KIND.ADJUSTMENT_DEBIT]: 'debit',
});
/**
 * Why a report's payment hold was released (D145).
 *
 * `manual` — a holder of `payments.record` released it with a note (Phase 10).
 * `work_order_settled` — released automatically, in the transaction of the
 * payment allocation or credit adjustment that settled the report's work
 * order. A release is never undone. A Phase 10 release has no stored basis and
 * is read as `manual`.
 */
export const PAYMENT_HOLD_RELEASE_BASIS = {
    MANUAL: 'manual',
    WORK_ORDER_SETTLED: 'work_order_settled',
};
export const ALL_PAYMENT_HOLD_RELEASE_BASES = Object.freeze(Object.values(PAYMENT_HOLD_RELEASE_BASIS));
// ---------------------------------------------------------------------------
// Invoice lifecycle (D152)
// ---------------------------------------------------------------------------
/**
 * The transitions an invoice may make, and the only ones. `cancelled` and
 * `discarded` are terminal. A draft→draft content edit is not a transition and
 * is not listed; it bumps the draft revision instead.
 */
export const INVOICE_TRANSITIONS = Object.freeze({
    [INVOICE_STATUS.DRAFT]: Object.freeze([
        INVOICE_STATUS.ISSUED,
        INVOICE_STATUS.DISCARDED,
    ]),
    [INVOICE_STATUS.ISSUED]: Object.freeze([INVOICE_STATUS.CANCELLED]),
    [INVOICE_STATUS.CANCELLED]: Object.freeze([]),
    [INVOICE_STATUS.DISCARDED]: Object.freeze([]),
});
export function invoiceTransitionAllowed(from, to) {
    return INVOICE_TRANSITIONS[from].includes(to);
}
/** Only a draft's lines, discounts, notes and due date may change. */
export function invoiceIsEditable(status) {
    return status === INVOICE_STATUS.DRAFT;
}
// ---------------------------------------------------------------------------
// Limits shared by validation and the backend
// ---------------------------------------------------------------------------
/** `numeric(12,3)`: at most three decimals, at most nine integer digits. */
export const QUANTITY_DECIMALS = 3;
export const QUANTITY_INTEGER_DIGITS = 9;
/** `numeric(5,2)`: a percentage carries at most two decimals. */
export const PERCENT_DECIMALS = 2;
export const MAX_INVOICE_LINES = 200;
export const MAX_PAYMENT_ALLOCATIONS = 200;
/** `tenant_settings.payment_terms_days` is 0–365 (D152). Its DEFAULT is the
 *  backend's alone and is deliberately not restated here (CLAUDE.md 6.1). */
export const MIN_PAYMENT_TERMS_DAYS = 0;
export const MAX_PAYMENT_TERMS_DAYS = 365;
// ---------------------------------------------------------------------------
// Exact decimal text
// ---------------------------------------------------------------------------
const DECIMAL_TEXT = /^(\d+)(?:\.(\d+))?$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * A non-negative decimal — text such as `"2.500"`, or a number such as `2.5` —
 * as an exact integer scaled by `10^scale` (`"2.5"`, scale 3 → `2500n`).
 *
 * Throws `RangeError` for anything that is not a plain non-negative decimal,
 * or that carries more significant decimals than `scale` (`"2.0001"` at scale
 * 3). Trailing zeros beyond the scale are accepted, because they do not change
 * the value (`"2.5000"` is `2500n`). A number is read through its shortest
 * decimal spelling, so `1.005` is exactly `1005n` at scale 3.
 */
export function parseDecimalText(value, scale) {
    let text;
    if (typeof value === 'number') {
        if (!Number.isFinite(value) || value < 0) {
            throw new RangeError(`Not a non-negative decimal: ${String(value)}`);
        }
        text = String(value);
    }
    else {
        text = value.trim();
    }
    const match = DECIMAL_TEXT.exec(text);
    if (!match)
        throw new RangeError(`Not a non-negative decimal: ${text}`);
    const whole = match[1] ?? '0';
    const fraction = (match[2] ?? '').replace(/0+$/, '');
    if (fraction.length > scale) {
        throw new RangeError(`More than ${scale} decimal places: ${text}`);
    }
    return BigInt(whole + fraction.padEnd(scale, '0'));
}
/** The canonical text of a scaled decimal: `2500n`, scale 3 → `"2.500"`, the
 *  spelling PostgreSQL returns for `numeric(12,3)`. */
export function formatDecimalText(scaled, scale) {
    if (scaled < 0n)
        throw new RangeError('A negative decimal is not expected here');
    const digits = scaled.toString().padStart(scale + 1, '0');
    if (scale === 0)
        return digits;
    return `${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
}
/** `"02.5"`, scale 3 → `"2.500"`. Throws like `parseDecimalText`. */
export function normalizeDecimalText(value, scale) {
    return formatDecimalText(parseDecimalText(value, scale), scale);
}
/** Compare two decimals exactly: −1, 0 or 1. */
export function compareDecimalText(a, b, scale = QUANTITY_DECIMALS) {
    const left = parseDecimalText(a, scale);
    const right = parseDecimalText(b, scale);
    return left < right ? -1 : left > right ? 1 : 0;
}
/** A quantity in the database's own spelling: `2.5` → `"2.500"`. */
export function normalizeQuantity(value) {
    return normalizeDecimalText(value, QUANTITY_DECIMALS);
}
/** A percentage in the database's own spelling: `15` → `"15.00"`. */
export function normalizePercent(value) {
    return normalizeDecimalText(value, PERCENT_DECIMALS);
}
// ---------------------------------------------------------------------------
// Exact money arithmetic
// ---------------------------------------------------------------------------
/**
 * A money value from the database (a `bigint` column may arrive as text, a
 * number or a bigint) as a JSON-safe integer number of minor units.
 *
 * Throws `RangeError` rather than return a figure JavaScript cannot represent
 * exactly: an API that sends a rounded total is worse than one that refuses
 * (D159).
 */
export function toSafeMinor(value) {
    const asNumber = typeof value === 'number' ? value : Number(value);
    if (typeof value === 'string' && !/^-?\d+$/.test(value.trim())) {
        throw new RangeError(`Not an integer number of minor units: ${value}`);
    }
    if (!Number.isSafeInteger(asNumber)) {
        throw new RangeError('A money value is outside the safe integer range');
    }
    if (typeof value === 'bigint' && BigInt(asNumber) !== value) {
        throw new RangeError('A money value is outside the safe integer range');
    }
    return asNumber;
}
function assertMinor(value, label) {
    if (!Number.isSafeInteger(value) || value < 0) {
        throw new RangeError(`${label} must be a non-negative safe integer of minor units`);
    }
}
function safeResult(value, label) {
    if (value > BigInt(Number.MAX_SAFE_INTEGER)) {
        throw new RangeError(`${label} is outside the safe integer range`);
    }
    return Number(value);
}
/** Round a non-negative `numerator / denominator` HALF UP to an integer. */
function divideHalfUp(numerator, denominator) {
    const quotient = numerator / denominator;
    const remainder = numerator % denominator;
    return remainder * 2n >= denominator ? quotient + 1n : quotient;
}
const QUANTITY_FACTOR = 10n ** BigInt(QUANTITY_DECIMALS);
/** Percent × 10^PERCENT_DECIMALS, divided by 100. */
const PERCENT_FACTOR = 100n * 10n ** BigInt(PERCENT_DECIMALS);
/**
 * quantity × unit price, rounded half up to a whole minor unit — exactly.
 * `"2.5"` × 101 → 253; `"1.005"` × 100 → 101.
 */
export function multiplyQuantityByUnitPrice(quantity, unitPriceMinor) {
    assertMinor(unitPriceMinor, 'The unit price');
    const scaled = parseDecimalText(quantity, QUANTITY_DECIMALS);
    return safeResult(divideHalfUp(scaled * BigInt(unitPriceMinor), QUANTITY_FACTOR), 'The line subtotal');
}
/**
 * `percent` of an amount, rounded half up to a whole minor unit — exactly.
 * `12.5%` of 1005 → 126 (125.625).
 */
export function percentOfMinor(amountMinor, percent) {
    assertMinor(amountMinor, 'The amount');
    const scaled = parseDecimalText(percent, PERCENT_DECIMALS);
    return safeResult(divideHalfUp(BigInt(amountMinor) * scaled, PERCENT_FACTOR), 'The percentage amount');
}
/**
 * A discount larger than the amount it applies to.
 *
 * The backend maps it to `400 VALIDATION_FAILED` with detail code
 * `discount_exceeds_amount` on `field` — `lines[i].discount` or
 * `documentDiscount`. It is thrown instead of clamping, because an oversized
 * discount that quietly becomes a smaller one is a reduction nobody approved;
 * the approved procedure for that is a recorded adjustment (D154).
 */
export class DiscountExceedsAmountError extends Error {
    name = 'DiscountExceedsAmountError';
    code = 'discount_exceeds_amount';
    target;
    /** The request field to name in the refusal. */
    field;
    /** What the discount would have applied to. */
    amountMinor;
    /** The discount as asked for (a percentage above 100 included). */
    discountMinor;
    constructor(target, amountMinor, discountMinor) {
        const field = target.kind === 'line' ? `lines[${target.lineIndex}].discount` : 'documentDiscount';
        super(`The discount on ${field} exceeds the amount it applies to.`);
        this.target = target;
        this.field = field;
        this.amountMinor = amountMinor;
        this.discountMinor = discountMinor;
    }
}
/** True for a `DiscountExceedsAmountError`, also across two copies of this
 *  package (the ESM and CommonJS builds are separate classes). */
export function isDiscountExceedsAmountError(error) {
    return (error instanceof DiscountExceedsAmountError ||
        (error instanceof Error &&
            error.name === 'DiscountExceedsAmountError' &&
            error.code === 'discount_exceeds_amount'));
}
/**
 * The discount, in minor units, on `amountMinor`.
 *
 * An ABSENT discount (both parts null or missing) is no discount. Both parts at
 * once is ambiguous and throws `RangeError`. A percentage above 100, or an
 * amount above `amountMinor`, throws `DiscountExceedsAmountError`. Exactly 100%
 * and an amount EQUAL to the amount are allowed: they take the line to zero,
 * which is a real, deliberate discount.
 */
export function invoiceDiscountFor(amountMinor, discount, target) {
    assertMinor(amountMinor, 'The discounted amount');
    if (!discount)
        return 0;
    const percent = discount.percent ?? null;
    const fixed = discount.amountMinor ?? null;
    if (percent === null && fixed === null)
        return 0;
    if (percent !== null && fixed !== null) {
        throw new RangeError('A discount is either a percentage or an amount, never both.');
    }
    if (percent !== null) {
        const scaled = parseDecimalText(percent, PERCENT_DECIMALS);
        const discountMinor = percentOfMinor(amountMinor, percent);
        if (scaled > 100n * 10n ** BigInt(PERCENT_DECIMALS)) {
            throw new DiscountExceedsAmountError(target, amountMinor, discountMinor);
        }
        return discountMinor;
    }
    assertMinor(fixed, 'The discount amount');
    if (fixed > amountMinor) {
        throw new DiscountExceedsAmountError(target, amountMinor, fixed);
    }
    return fixed;
}
/**
 * The whole invoice, in the quotation's order (D43, D151): each line's
 * subtotal and discount, the document discount on what the lines leave, and
 * the tax on what remains after that.
 *
 * Throws `DiscountExceedsAmountError` naming the first line (by index) or the
 * document whose discount exceeds its amount — never clamps. Throws
 * `RangeError` for malformed input (a negative price, a rate above 100, a
 * quantity with four decimals, both discount parts at once, a figure outside
 * the safe integer range).
 */
export function computeInvoiceTotals(input) {
    const taxRate = parseDecimalText(input.taxRatePercent, PERCENT_DECIMALS);
    if (taxRate > 100n * 10n ** BigInt(PERCENT_DECIMALS)) {
        throw new RangeError('A tax rate is at most 100 percent');
    }
    const lines = [];
    let subtotal = 0n;
    let lineDiscount = 0n;
    input.lines.forEach((line, lineIndex) => {
        const subtotalMinor = multiplyQuantityByUnitPrice(line.quantity, line.unitPriceMinor);
        const discountMinor = invoiceDiscountFor(subtotalMinor, line.discount, {
            kind: 'line',
            lineIndex,
        });
        lines.push({ subtotalMinor, discountMinor, totalMinor: subtotalMinor - discountMinor });
        subtotal += BigInt(subtotalMinor);
        lineDiscount += BigInt(discountMinor);
    });
    const subtotalMinor = safeResult(subtotal, 'The invoice subtotal');
    const lineDiscountMinor = safeResult(lineDiscount, 'The line discounts');
    const afterLines = subtotalMinor - lineDiscountMinor;
    const documentDiscountMinor = invoiceDiscountFor(afterLines, input.documentDiscount, {
        kind: 'document',
    });
    const taxableMinor = afterLines - documentDiscountMinor;
    const taxMinor = percentOfMinor(taxableMinor, input.taxRatePercent);
    const totalMinor = safeResult(BigInt(taxableMinor) + BigInt(taxMinor), 'The invoice total');
    return {
        lines,
        subtotalMinor,
        lineDiscountMinor,
        documentDiscountMinor,
        taxableMinor,
        taxMinor,
        totalMinor,
    };
}
/**
 * The payment position of an invoice.
 *
 * Only an ISSUED invoice has one. For every other status all three figures
 * are 0 and the status is null — the same answer as the SQL
 * `lims_invoice_balance`, which is 0 for a non-issued invoice: nothing is owed
 * on a draft, and a cancelled invoice has had its effect reversed on the
 * account (its `invoice_cancelled` entry).
 *
 * `paid` when the balance is 0 — which includes an invoice whose total is 0 and
 * one a credit adjustment brought to 0: nothing remains due. `partially_paid`
 * when a payment covers part of it. `unpaid` when no payment covers any of it,
 * even if a credit adjustment has reduced it: an adjustment is not a payment.
 */
export function deriveInvoicePaymentStatus(input) {
    assertMinor(input.totalMinor, 'The invoice total');
    assertMinor(input.debitMinor, 'The debit adjustments');
    assertMinor(input.creditMinor, 'The credit adjustments');
    assertMinor(input.allocatedMinor, 'The allocations');
    if (input.status !== INVOICE_STATUS.ISSUED) {
        return { amountDueMinor: 0, settledMinor: 0, balanceMinor: 0, paymentStatus: null };
    }
    const amountDueMinor = input.totalMinor + input.debitMinor - input.creditMinor;
    const settledMinor = input.allocatedMinor;
    const balanceMinor = amountDueMinor - settledMinor;
    if (!Number.isSafeInteger(amountDueMinor) || !Number.isSafeInteger(balanceMinor)) {
        throw new RangeError('An invoice figure is outside the safe integer range');
    }
    const paymentStatus = balanceMinor <= 0
        ? PAYMENT_STATUS.PAID
        : settledMinor > 0
            ? PAYMENT_STATUS.PARTIALLY_PAID
            : PAYMENT_STATUS.UNPAID;
    return { amountDueMinor, settledMinor, balanceMinor, paymentStatus };
}
/**
 * Is this invoice overdue, as of `today` (the laboratory's own calendar date,
 * from the API's clock — D81/D90)?
 *
 * Issued, with a balance, and PAST its due date: an invoice due today is not
 * overdue until tomorrow.
 */
export function invoiceIsOverdue(input) {
    if (input.status !== INVOICE_STATUS.ISSUED)
        return false;
    if (input.balanceMinor <= 0)
        return false;
    if (input.dueDate === null)
        return false;
    return input.dueDate < input.today;
}
// ---------------------------------------------------------------------------
// Calendar dates
// ---------------------------------------------------------------------------
function parseIsoDate(value) {
    const match = ISO_DATE.exec(value);
    if (!match)
        throw new RangeError(`Not an ISO date (YYYY-MM-DD): ${value}`);
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (year < 1 ||
        probe.getUTCFullYear() !== year ||
        probe.getUTCMonth() !== month - 1 ||
        probe.getUTCDate() !== day) {
        throw new RangeError(`The date does not exist in the calendar: ${value}`);
    }
    return { year, month, day };
}
function formatIsoDate(date) {
    const year = String(date.getUTCFullYear()).padStart(4, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
/** A calendar date plus (or minus) whole days. Calendar arithmetic only — no
 *  timezone is involved, because the input is already a laboratory's date. */
export function addDaysToIsoDate(date, days) {
    if (!Number.isInteger(days))
        throw new RangeError('Days must be a whole number');
    const { year, month, day } = parseIsoDate(date);
    const moved = new Date(Date.UTC(year, month - 1, day));
    moved.setUTCDate(moved.getUTCDate() + days);
    return formatIsoDate(moved);
}
/**
 * The due date an invoice gets at issuance when its draft named none: the
 * issue date plus the laboratory's `payment_terms_days` (D152). Zero days is
 * "due on issue". Never before the issue date.
 */
export function defaultDueDate(issueDate, paymentTermsDays) {
    if (!Number.isInteger(paymentTermsDays) ||
        paymentTermsDays < MIN_PAYMENT_TERMS_DAYS ||
        paymentTermsDays > MAX_PAYMENT_TERMS_DAYS) {
        throw new RangeError(`Payment terms are ${MIN_PAYMENT_TERMS_DAYS}–${MAX_PAYMENT_TERMS_DAYS} whole days`);
    }
    return addDaysToIsoDate(issueDate, paymentTermsDays);
}
/**
 * The start of the default statement period ending on `to`: one year back,
 * plus one day, so the period is exactly one year long (`2026-09-19` →
 * `2025-09-20`). A 29 February with no counterpart counts back from 28
 * February. The staff statement and the portal use this one rule.
 */
export function defaultStatementFrom(to) {
    const { year, month, day } = parseIsoDate(to);
    const lastDay = new Date(Date.UTC(year - 1, month, 0)).getUTCDate();
    const yearBack = new Date(Date.UTC(year - 1, month - 1, Math.min(day, lastDay)));
    yearBack.setUTCDate(yearBack.getUTCDate() + 1);
    return formatIsoDate(yearBack);
}
function entryDocumentId(entry) {
    return entry.invoiceId ?? entry.paymentId ?? entry.adjustmentId ?? '';
}
function compareInstants(a, b) {
    const left = Date.parse(a);
    const right = Date.parse(b);
    if (Number.isFinite(left) && Number.isFinite(right)) {
        return left < right ? -1 : left > right ? 1 : 0;
    }
    return a < b ? -1 : a > b ? 1 : 0;
}
/** Statement order: entry date, then when it was recorded, then kind, then the
 *  document's id — the order the view is read in. */
export function compareAccountEntries(a, b) {
    if (a.entryDate !== b.entryDate)
        return a.entryDate < b.entryDate ? -1 : 1;
    const byInstant = compareInstants(a.occurredAt, b.occurredAt);
    if (byInstant !== 0)
        return byInstant;
    if (a.kind !== b.kind)
        return a.kind < b.kind ? -1 : 1;
    const left = entryDocumentId(a);
    const right = entryDocumentId(b);
    return left < right ? -1 : left > right ? 1 : 0;
}
/**
 * A client's statement for a period, from the entries of ONE client in ONE
 * currency (D155).
 *
 * Opening balance = every entry before `from`; the rows are the entries dated
 * `from`…`to`, each with the running balance; closing balance = every entry up
 * to `to`. Entries after `to` are ignored. A debit is owed BY the client, so a
 * positive balance is what the client owes and a negative one is credit on the
 * account (an unallocated payment, for instance).
 *
 * The entries are put in statement order here (`compareAccountEntries`), so the
 * answer never depends on the order they were read in. Throws `RangeError` for
 * a malformed period, an entry with money on the wrong side, or a figure
 * outside the safe integer range.
 */
export function buildStatement(entries, period) {
    parseIsoDate(period.from);
    parseIsoDate(period.to);
    if (period.to < period.from) {
        throw new RangeError('A statement period ends on or after the day it starts');
    }
    const totals = {
        invoicedMinor: 0,
        cancelledMinor: 0,
        paidMinor: 0,
        reversedMinor: 0,
        creditsMinor: 0,
        debitsMinor: 0,
    };
    const totalFor = {
        invoice_issued: 'invoicedMinor',
        invoice_cancelled: 'cancelledMinor',
        payment_received: 'paidMinor',
        payment_reversed: 'reversedMinor',
        adjustment_credit: 'creditsMinor',
        adjustment_debit: 'debitsMinor',
    };
    const ordered = [...entries].sort(compareAccountEntries);
    let running = 0;
    let openingBalanceMinor = 0;
    const rows = [];
    const checked = (value) => {
        if (!Number.isSafeInteger(value)) {
            throw new RangeError('A statement figure is outside the safe integer range');
        }
        return value;
    };
    for (const entry of ordered) {
        assertMinor(entry.debitMinor, 'A statement debit');
        assertMinor(entry.creditMinor, 'A statement credit');
        const side = ACCOUNT_ENTRY_SIDE[entry.kind];
        if (side === undefined)
            throw new RangeError(`Unknown entry kind: ${String(entry.kind)}`);
        const wrongSide = side === 'debit' ? entry.creditMinor : entry.debitMinor;
        if (wrongSide !== 0) {
            throw new RangeError(`A ${entry.kind} entry carries money on the ${side === 'debit' ? 'credit' : 'debit'} side`);
        }
        if (entry.entryDate > period.to)
            continue;
        running = checked(running + entry.debitMinor - entry.creditMinor);
        if (entry.entryDate < period.from) {
            openingBalanceMinor = running;
            continue;
        }
        const key = totalFor[entry.kind];
        totals[key] = checked(totals[key] + entry.debitMinor + entry.creditMinor);
        rows.push({ ...entry, balanceMinor: running });
    }
    return { openingBalanceMinor, rows, closingBalanceMinor: running, totals };
}
/**
 * Is this work order SETTLED (D145)?
 *
 * Every non-cancelled service invoiced IN FULL on issued invoices, and every
 * issued, uncancelled invoice carrying a line of the work order at a balance
 * of 0. Cancelled services are ignored. A work order with no non-cancelled
 * service is NOT settled: there is nothing that was paid for, and releasing a
 * hold on that basis would release it on no payment at all. The rule is
 * deliberately strict — never a partial payment.
 */
export function workOrderIsSettled(input) {
    const live = input.services.filter((service) => !service.cancelled);
    if (live.length === 0)
        return false;
    for (const service of live) {
        if (compareDecimalText(service.invoicedIssuedQuantity, service.quantity) < 0) {
            return false;
        }
    }
    return input.invoiceBalances.every((balance) => balance === 0);
}
export const INVOICE_LABORATORY_SNAPSHOT_FIELDS = Object.freeze([
    'name',
    'address',
    'contactEmail',
    'contactPhone',
    'vatRegistrationNumber',
    'commercialRegistrationNumber',
]);
export const INVOICE_CLIENT_SNAPSHOT_FIELDS = Object.freeze([
    'number',
    'name',
    'address',
    'city',
    'vatRegistrationNumber',
    'commercialRegistrationNumber',
]);
//# sourceMappingURL=finance.js.map